import { ErrorMessage, Field, useField } from 'formik';
import { forwardRef, useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { AiOutlineEye, AiOutlineEyeInvisible } from 'react-icons/ai';

import { PasswordValidationMessages } from './PasswordValidationMessages';

type PropTypes = {
	name: string;
	label: string;
	type: 'email' | 'password' | 'text' | 'tel' | 'textarea' | 'recaptcha';
	placeholder?: string;
	error?: boolean;
	touched?: boolean;
	disabled?: boolean;
	length?: number;
	enableCustomValidations?: boolean;
	recaptchaSiteKey?: string;
};

const AuthInput = forwardRef<ReCAPTCHA, PropTypes>(
	(
		{
			name,
			label,
			type,
			placeholder,
			error,
			touched,
			disabled = false,
			enableCustomValidations = false,
			length,
			recaptchaSiteKey,
			...props
		},
		ref,
	) => {
		const [field, meta, helpers] = useField(name);
		const currentValue = field.value;
		const [showPassword, setShowPassword] = useState(false);

		const handleRecaptchaChange = (value: string | null) => {
			helpers.setValue(value || '');
		};

		return (
			<div className="flex flex-col w-full relative min-h-14 autofill:font-mono">
				<div className="relative">
					{type === 'recaptcha' ? (
						<div className="flex flex-col items-center w-full overflow-hidden">
							<div className="transform scale-75 sm:scale-100 origin-center">
								<ReCAPTCHA
									ref={ref}
									sitekey={
										recaptchaSiteKey ||
										import.meta.env.VITE_RECAPTCHA_CLIENT_KEY
									}
									onChange={handleRecaptchaChange}
									{...props}
								/>
							</div>
						</div>
					) : type === 'textarea' ? (
						<textarea
							className="block w-full h-20 p-2 bg-ui-bg-field border rounded-md appearance-none focus:outline-none focus:ring-0 focus:shadow-borders-interactive-with-active border-ui-border-base hover:bg-ui-bg-field-hover"
							id={name}
							placeholder={placeholder || ''}
							disabled={disabled}
							{...field}
							{...props}
						/>
					) : (
						<>
							<Field
								className="block glassy-input"
								type={
									type === 'password'
										? showPassword
											? 'text'
											: 'password'
										: type
								}
								id={name}
								name={name}
								maxLength={length ? length : (type === 'tel' && 6) || 'false'}
								placeholder={placeholder || ''}
								data-error={error && touched}
								disabled={disabled}
								{...props}
							/>
							{type === 'password' && (
								<button
									type="button"
									className="absolute inset-y-0 right-0 pr-3 flex items-center"
									onClick={() => setShowPassword(!showPassword)}
									tabIndex={-1}
								>
									{showPassword ? (
										<AiOutlineEyeInvisible className="h-5 w-5 text-gray-500" />
									) : (
										<AiOutlineEye className="h-5 w-5 text-gray-500" />
									)}
								</button>
							)}
						</>
					)}
				</div>
				{!enableCustomValidations && (
					<div
						className="w-full h-3 mb-3"
						data-test={`form-input-error-${name}`}
					>
						<ErrorMessage
							className="text-red-500 text-xs w-full"
							name={name}
							component="p"
						/>
					</div>
				)}
				{enableCustomValidations && (
					<div className="w-full mb-2">
						{meta.error ? (
							<PasswordValidationMessages password={currentValue} />
						) : (
							<div className="h-3" />
						)}
					</div>
				)}
			</div>
		);
	},
);

AuthInput.displayName = 'AuthInput';

export default AuthInput;
